import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Grid,
  TextField,
  MenuItem,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  IconButton,
  Collapse,
  Typography,
  CircularProgress,
  Stack,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DownloadIcon from "@mui/icons-material/Download";
const lucy = require("../../../helpers/lucy_service");

const CATEGORIES = [
  { v: "", l: "Todas las categorías" },
  { v: "cuota", l: "Cuota" },
  { v: "acceso-admin", l: "Acceso de administradores" },
  { v: "sesion", l: "Sesión" },
  { v: "actividad", l: "Actividad" },
  { v: "datos", l: "Datos" },
];
const ACTORS = [
  { v: "", l: "Cualquier actor" },
  { v: "user", l: "El propio usuario" },
  { v: "admin", l: "Un administrador" },
  { v: "system", l: "Sistema" },
];
const PAGE_SIZES = [25, 50, 100];

const catColor = (c) =>
  ({
    cuota: "primary",
    "acceso-admin": "warning",
    sesion: "info",
    actividad: "default",
    datos: "error",
  }[c] || "default");

const AuditRow = ({ ev }) => {
  const [open, setOpen] = useState(false);
  const has = ev.before || ev.after || ev.metadata;
  return (
    <>
      <TableRow hover>
        <TableCell padding="checkbox">
          {has && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{new Date(ev.ts).toLocaleString("es-GT")}</TableCell>
        <TableCell>
          <Chip size="small" label={ev.category} color={catColor(ev.category)} />
        </TableCell>
        <TableCell>{ev.eventType}</TableCell>
        <TableCell>
          {ev.actorName || ev.actorId}
          <Typography variant="caption" color="text.secondary" display="block">
            {ev.actorType}
            {ev.ip ? ` · ${ev.ip}` : ""}
          </Typography>
        </TableCell>
        <TableCell>{ev.summary}</TableCell>
      </TableRow>
      {has && (
        <TableRow>
          <TableCell colSpan={6} sx={{ py: 0, border: 0 }}>
            <Collapse in={open} unmountOnExit>
              <Box
                component="pre"
                sx={{
                  m: 1,
                  p: 1.5,
                  bgcolor: "action.hover",
                  borderRadius: 1,
                  fontSize: 12,
                  overflowX: "auto",
                }}
              >
                {JSON.stringify(
                  { before: ev.before, after: ev.after, metadata: ev.metadata },
                  null,
                  2
                )}
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

const AuditTab = ({ sub }) => {
  const [f, setF] = useState({
    from: "",
    to: "",
    category: "",
    tipo: "",
    actor: "",
    q: "",
    sort: "desc",
    pageSize: 25,
  });
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [next, setNext] = useState(null);
  const [stack, setStack] = useState([]);
  const [loading, setLoading] = useState(false);

  const query = useCallback(
    async (c) => {
      setLoading(true);
      try {
        const filters = { ...f };
        if (filters.from) filters.from = new Date(filters.from).toISOString();
        if (filters.to) filters.to = new Date(filters.to).toISOString();
        const res = await lucy.getUserAuditLog(sub, { ...filters, cursor: c });
        setRows(res.data.response.items || []);
        setNext(res.data.response.nextCursor || null);
      } finally {
        setLoading(false);
      }
    },
    [sub, f]
  );

  useEffect(() => {
    query(null);
    setCursor(null);
    setStack([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sub]);

  const applyFilters = () => {
    setCursor(null);
    setStack([]);
    query(null);
  };

  const exportCsv = async () => {
    const filters = { ...f };
    if (filters.from) filters.from = new Date(filters.from).toISOString();
    if (filters.to) filters.to = new Date(filters.to).toISOString();
    const blob = await lucy.downloadUserAuditCsv(sub, filters);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `auditoria-${sub}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  return (
    <Box>
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Desde"
            InputLabelProps={{ shrink: true }}
            value={f.from}
            onChange={set("from")}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField
            fullWidth
            size="small"
            type="date"
            label="Hasta"
            InputLabelProps={{ shrink: true }}
            value={f.to}
            onChange={set("to")}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField
            select
            fullWidth
            size="small"
            label="Categoría"
            value={f.category}
            onChange={set("category")}
          >
            {CATEGORIES.map((c) => (
              <MenuItem key={c.v} value={c.v}>
                {c.l}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField
            select
            fullWidth
            size="small"
            label="Actor"
            value={f.actor}
            onChange={set("actor")}
          >
            {ACTORS.map((a) => (
              <MenuItem key={a.v} value={a.v}>
                {a.l}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Tipo (ej. quota.extended)"
            value={f.tipo}
            onChange={set("tipo")}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField
            fullWidth
            size="small"
            label="Buscar en resumen"
            value={f.q}
            onChange={set("q")}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField
            select
            fullWidth
            size="small"
            label="Orden"
            value={f.sort}
            onChange={set("sort")}
          >
            <MenuItem value="desc">Más recientes primero</MenuItem>
            <MenuItem value="asc">Más antiguos primero</MenuItem>
          </TextField>
        </Grid>
        <Grid item xs={6} md={2}>
          <TextField
            select
            fullWidth
            size="small"
            label="Por página"
            value={f.pageSize}
            onChange={set("pageSize")}
          >
            {PAGE_SIZES.map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} md={8}>
          <Stack direction="row" spacing={1}>
            <Button variant="contained" onClick={applyFilters}>
              Aplicar filtros
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setF({
                  from: "",
                  to: "",
                  category: "cuota",
                  tipo: "",
                  actor: "",
                  q: "",
                  sort: "desc",
                  pageSize: 25,
                });
              }}
            >
              Solo cuota
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                setF((p) => ({ ...p, category: "acceso-admin", tipo: "", q: "" }));
              }}
            >
              Solo accesos de admin
            </Button>
            <Button startIcon={<DownloadIcon />} onClick={exportCsv}>
              Exportar CSV
            </Button>
          </Stack>
        </Grid>
      </Grid>

      {loading ? (
        <CircularProgress />
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Fecha</TableCell>
                <TableCell>Categoría</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Actor</TableCell>
                <TableCell>Resumen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((ev) => (
                <AuditRow key={ev.eventId || ev.tsEvent} ev={ev} />
              ))}
              {!rows.length && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Sin eventos para los filtros seleccionados
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      )}

      <Box sx={{ display: "flex", gap: 1, mt: 2 }}>
        <Button
          size="small"
          disabled={!stack.length}
          onClick={() => {
            const prev = [...stack];
            const to = prev.pop();
            setStack(prev);
            setCursor(to || null);
            query(to || null);
          }}
        >
          Anterior
        </Button>
        <Button
          size="small"
          disabled={!next}
          onClick={() => {
            setStack([...stack, cursor]);
            setCursor(next);
            query(next);
          }}
        >
          Siguiente
        </Button>
      </Box>
    </Box>
  );
};

export default AuditTab;
